import { UNAUTHED_ERR_MSG } from "./const.js";

/**
 * Message patterns for expected control-flow throws: business rules (out of
 * credits), input validation, auth guards, and the shot-list discard-confirm
 * dialog. These are user-facing signals raised as errors, not app faults —
 * telemetry suppresses them so they don't inflate error dashboards or mask
 * real faults. One canonical list shared by the server filter
 * (server/posthogClient.ts) and the client classifier
 * (client/src/lib/posthogExceptions.ts) so the two cannot drift.
 *
 * Every pattern is anchored to the exact app phrasing, for two reasons:
 * - a genuine bug whose message merely CONTAINS one of these phrases must
 *   still be captured (e.g. "Cannot assign in required field" embeds
 *   "sign in required");
 * - drained render vendors surface raw "Insufficient credits" errors of their
 *   own (see server/pixverseBalanceGuard.ts) — those are real operational
 *   faults, so only the app's own credit-guard phrasings may match.
 *
 * Deliberately NOT listed: "temporarily unavailable" — that is the app's
 * provider-outage phrasing (pixverseBalanceGuard, youtubeDownloader,
 * chatRoute, directorRouter). Suppressing it would blind error tracking
 * during a full provider outage (the Jul 17 Gemini spend-cap class); those
 * captures stay, rate-bounded by captureServerException's per-signature
 * cooldown.
 */
export const EXPECTED_CONTROL_FLOW_MESSAGE_PATTERNS: readonly RegExp[] = [
  // Out-of-credits entitlement guards — exact app phrasings only (see above).
  /^Insufficient credits\. You have /i,
  /^Insufficient credits for the remaining /i,
  // Shot-list discard confirmation dialog. These literal strings are a frozen
  // wire contract — deployed clients regex-match them (directorGenericJobs).
  /would discard \d+ existing storyboard frame/i,
  /\bconfirmDiscardStoryboard\b/i,
  // Auth guards.
  /^Sign in required/i,
  // OTP validation.
  /^Invalid or expired verification code/i,
  // Audio input validation.
  /^That link opens a web page, not an audio file/i,
  /^Invalid audio URL/i,
  // Upload size validation — covers "Image file is too large…",
  // "Audio file is too large…" (vocalRemover) and "Image too large.
  // Maximum size is 5MB." (routers) from the same guard family.
  /^(?:Image|Audio)(?: file)?(?: is)? too large\b/i,
];

/**
 * True when `message` is the exact phrasing of a known expected control-flow
 * throw. The canonical auth-guard message (UNAUTHED_ERR_MSG, thrown by every
 * protectedProcedure) is matched via the constant so copy changes track
 * automatically.
 */
export function isExpectedControlFlowMessage(message: string): boolean {
  if (!message) return false;
  if (message.startsWith(UNAUTHED_ERR_MSG)) return true;
  return EXPECTED_CONTROL_FLOW_MESSAGE_PATTERNS.some((re) => re.test(message));
}
